import React from "react";
import "./tournament-round-hole-assignment-list-item.scss";
import {faPlus, faTrash, faUsersViewfinder} from "@fortawesome/free-solid-svg-icons";
import {
  CENTER,
  SPACE_BETWEEN,
  START,
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
  golfstatusSpacing,
  GSButton,
  GSItemList,
  GSLoadingSpinnerOverlay
} from "golfstatus_react_components";
import {getTime} from "../../../../helpers/Dates";
import TournamentRoundHoleAssignmentTeamItem from "./tournament-round-hole-assignment-team-item";
import {selectCurrentTeam, setCurrentTeam} from "../../../../reducers/teamsSlice";
import {
  saveRoundHoleAssignment,
  selectCurrentHoleAssignment,
  selectCurrentRound,
  selectCurrentRoundHoleAssignments,
  selectLoading,
  selectPendingHoleAssignment,
  selectRecentlyAssignedTeams,
  setCurrentHoleAssignment,
  setCurrentRoundHoleAssignment,
  setPendingHoleAssignment,
  setPendingTeams,
  setRecentlyAssignedTeams,
  setRecentlyUnassignedTeams,
  swapRoundHoleAssignments
} from "../../../../reducers/roundSlice";
import {useDispatch, useSelector} from "react-redux";

const TournamentRoundHoleAssignmentListItem = (props) => {
  const dispatch = useDispatch();

  const { holeAssignments, readOnly, assignedOnly } = props;

  // selectors
  const currentRound = useSelector(selectCurrentRound);
  const currentHoleAssignment = useSelector(selectCurrentHoleAssignment);
  const currentRoundHoleAssignments = useSelector(
    selectCurrentRoundHoleAssignments
  );

  //the selection is a snapshot taken when a spot was clicked, so it still
  //describes the teams that were on it then. the live record is what decides how
  //the spots around it look and what a click on one of them means.
  const selectedAssignment = {
    ...currentRoundHoleAssignments?.find?.(
      (rha) => rha?.id === currentHoleAssignment?.id
    ),
    grouping: currentHoleAssignment?.grouping,
  };
  const currentTournamentTeam = useSelector(selectCurrentTeam);
  const recentlyAssignedTeams = useSelector(selectRecentlyAssignedTeams);
  const pendingHoleAssignment = useSelector(selectPendingHoleAssignment);
  const roundLoading = useSelector(selectLoading);

  //the spot a team is on its way into holds the team's colors until the
  //assignment comes back
  const isPending = (holePosition, grouping) => {
    return (
      roundLoading?.some?.((l) =>
        ["saveRoundHoleAssignment", "swapRoundHoleAssignments"].includes(l)
      ) &&
      pendingHoleAssignment?.id === holePosition?.id &&
      pendingHoleAssignment?.grouping === grouping
    );
  };

  const selectHoleAssignment = (holeAssignment) => {
    if (currentTournamentTeam?.id) {
      if (holeAssignment?.roundHoleAssignmentTournamentTeams?.length > 0) {
        dispatch(setCurrentTeam({}));
        dispatch(setCurrentHoleAssignment(holeAssignment));
      } else {
        let updatedHoleAssignment = {
          ...holeAssignment,
          roundHoleAssignmentTournamentTeams: [
            {
              tournamentTeamKey: currentTournamentTeam?.id,
              roundHoleAssignmentKey: holeAssignment.id,
              grouping: holeAssignment.grouping
            },
          ],
        };
        //marks the card that is about to land in the spot
        dispatch(setRecentlyAssignedTeams([currentTournamentTeam?.id]));
        dispatch(setRecentlyUnassignedTeams([]));
        dispatch(setPendingTeams([currentTournamentTeam?.id]));
        dispatch(
          setPendingHoleAssignment({
            id: holeAssignment?.id,
            grouping: holeAssignment?.grouping,
          })
        );
        dispatch(saveRoundHoleAssignment(updatedHoleAssignment));
      }
    } else {
      if (holeAssignment?.id === currentHoleAssignment?.id && holeAssignment?.grouping === currentHoleAssignment?.grouping) {
        dispatch(setCurrentHoleAssignment({}));
      } else if (hasAssignedTeam(selectedAssignment, selectedAssignment?.grouping) &&
                !hasAssignedTeam(holeAssignment, holeAssignment?.grouping)) {
        dispatch(
          setPendingTeams([
            getAssignedTeam(selectedAssignment, selectedAssignment?.grouping)
              ?.tournamentTeamKey,
            getAssignedTeam(holeAssignment, holeAssignment?.grouping)
              ?.tournamentTeamKey,
          ])
        );
        dispatch(
          setPendingHoleAssignment({
            id: holeAssignment?.id,
            grouping: holeAssignment?.grouping,
          })
        );
        dispatch(
          setRecentlyAssignedTeams([
            getAssignedTeam(selectedAssignment, selectedAssignment?.grouping)
              ?.tournamentTeamKey,
            getAssignedTeam(holeAssignment, holeAssignment?.grouping)
              ?.tournamentTeamKey,
          ])
        );
        dispatch(setRecentlyUnassignedTeams([]));
        dispatch(swapRoundHoleAssignments({ assignments: [currentHoleAssignment, holeAssignment] }));
      } else {
        dispatch(setCurrentHoleAssignment(holeAssignment));
      }
    }
  };

  const assignTeamToHoleAssignment = (team, holeAssignment, grouping) => {
    const targetRhatt = getAssignedTeam(holeAssignment, grouping);
    const filteredRhatts = targetRhatt
      ? [...holeAssignment.roundHoleAssignmentTournamentTeams.filter((rhatt) => rhatt.id !== targetRhatt?.id)]
      : [...holeAssignment.roundHoleAssignmentTournamentTeams];
    const updatedHoleAssignment = {
      ...holeAssignment,
      roundHoleAssignmentTournamentTeams: [
        ...filteredRhatts,
        { tournamentTeamKey: team.id, roundHoleAssignmentKey: holeAssignment.id, grouping: grouping },
      ],
    };

    //the team taking the spot lands assigned, the one it replaces lands back in
    //the unassigned list
    dispatch(setRecentlyAssignedTeams([team?.id]));
    dispatch(setRecentlyUnassignedTeams([targetRhatt?.tournamentTeamKey]));
    dispatch(setPendingTeams([team?.id, targetRhatt?.tournamentTeamKey]));
    dispatch(setPendingHoleAssignment({ id: holeAssignment?.id, grouping }));
    dispatch(saveRoundHoleAssignment(updatedHoleAssignment));
  };

  //blocking works from the bottom up, so only the last open position can be
  //blocked and the a spot is the last one left.
  const openAssignments = holeAssignments?.filter?.((ha) => !ha?.locked) ?? [];

  const canBlock = (holePosition) => {
    return openAssignments?.[openAssignments.length - 1]?.id === holePosition?.id;
  };

  const toggleBlocked = (holePosition, e) => {
    e?.stopPropagation?.();
    //nothing is being moved, so no card or spot should read as pending
    dispatch(setPendingTeams([]));
    dispatch(setPendingHoleAssignment({}));
    const blocking = !holePosition?.locked;
    const update = {
      ...holePosition,
      locked: blocking,
      //a blocked spot can't hold a team, so anyone assigned to it goes back to
      //the unassigned teams list.
      roundHoleAssignmentTournamentTeams: blocking
        ? []
        : holePosition?.roundHoleAssignmentTournamentTeams,
    };
    dispatch(setCurrentRoundHoleAssignment(update));
    //the spot shows the change before the server has it, so a failed save puts
    //the spot and any team it held back the way they were
    dispatch(saveRoundHoleAssignment(update))
      .unwrap()
      .catch(() => dispatch(setCurrentRoundHoleAssignment(holePosition)));
  };

  const getBlockHole = (holePosition) => {
    if (readOnly) {
      return null;
    }

    const blockable = canBlock(holePosition);

    return (
      <GSButton
        type="transparent block-hole"
        hoverType="underline"
        buttonIcon={faTrash}
        isDisabled={!blockable}
        onClick={(e) => toggleBlocked(holePosition, e)}
      />
    );
  };

  //the trash blocks from the bottom up, so the first blocked position is always
  //the next group to open back up.
  const nextBlockedAssignment = holeAssignments?.find?.((ha) => ha?.locked);

  const getAddGroup = () => {
    if (readOnly || !nextBlockedAssignment) {
      return null;
    }

    //with every group blocked there is nothing else on the hole, so the icon
    //takes the grouping background to stay visible.
    const fullyBlocked = openAssignments?.length === 0;

    return (
      <GSButton
        type={`transparent add-group ${fullyBlocked ? "empty" : ""}`}
        hoverType="underline"
        style={fullyBlocked ? { backgroundColor: golfstatusColors.grey50 } : {}}
        buttonIcon={faPlus}
        onClick={(e) => toggleBlocked(nextBlockedAssignment, e)}
      />
    );
  };

  //blocked positions aren't shown, and handing them to the list anyway leaves an
  //empty item, and the gap after it, for each one. a fully blocked hole has no
  //list at all, so the add button sits right under the header.
  const getAssignments = () => {
    if (openAssignments.length === 0) {
      return null;
    }

    return (
      <GSItemList
        type="vertical medium-gap"
        items={openAssignments}
        listItem={(item) => getAssignment(item)}
      />
    );
  };

  const isSelected = (assignment, grouping) => {
    return currentHoleAssignment?.id !== undefined
        && currentHoleAssignment?.id === assignment?.id
        && currentHoleAssignment?.grouping === grouping;
  };

  const isAvailable = (assignment, grouping) => {
    return !isSelected(assignment, grouping) &&
           hasAssignedTeam(assignment, grouping)
             ? (currentHoleAssignment?.id !== undefined && !hasAssignedTeam(selectedAssignment, selectedAssignment?.grouping))
             : (currentTournamentTeam?.id !== undefined || hasAssignedTeam(selectedAssignment, selectedAssignment?.grouping));
  };

  const isSwappable = (assignment, grouping) => {
    return !isSelected(assignment, grouping)
        && hasAssignedTeam(assignment, grouping)
        && (currentHoleAssignment?.id !== undefined || currentTournamentTeam?.id !== undefined);
  };

  const hasAssignedTeam = (assignment, grouping) => {
    if (!assignment?.id || !grouping) {
      return false;
    }
    return getAssignedTeam(assignment, grouping) !== undefined;
  };

  const getAssignedTeam = (assignment, grouping) => {
    return assignment.roundHoleAssignmentTournamentTeams?.find((tt) => tt.grouping === grouping);
  };

  const getAssignment = (holePosition) => {
    // Do not display locked assignments
    if (holePosition.locked) {
      return;
    }

    let positions = [];
    for (let i=0; i < holePosition.maxTeamsPerHole; i++) {
      const grouping = i+1;
      const roundHoleAssignmentTournamentTeam = getAssignedTeam(holePosition, grouping);

      // state variables
      const selected = isSelected(holePosition, grouping);
      const swappable = isSwappable(holePosition, grouping);
      const available = isAvailable(holePosition, grouping);

      if (roundHoleAssignmentTournamentTeam) {
        positions.push(
          <div key={`${holePosition.id}-${grouping}`}
               style={{...defaultLayouts.fullWidth, ...defaultLayouts.vStack}}
               onClick={
                 readOnly
                   ? () => {}
                   : () => selectHoleAssignment({...holePosition, grouping})}
          >
            <TournamentRoundHoleAssignmentTeamItem
              team={{...roundHoleAssignmentTournamentTeam}}
              holeAssignment={{...holePosition}}
              assignTeamToHoleAssignment={assignTeamToHoleAssignment}
              assigned={true}
              justAssigned={recentlyAssignedTeams?.includes?.(
                roundHoleAssignmentTournamentTeam?.tournamentTeamKey
              )}
              available={available}
              readOnly={readOnly}
              selected={selected}
              swappable={swappable}
            />
          </div>
        );
      } else if (!assignedOnly) {
        const pending = isPending(holePosition, grouping);

        let classNames = "unassigned";
        if (pending) {
          classNames += " pending";
        } else if (selected) {
          classNames += " selected";
        } else if (available) {
          classNames += " available";
        }
        positions.push(
          <div key={`${holePosition.id}-${grouping}`} className={classNames}>
            {pending && (
              <GSLoadingSpinnerOverlay
                spinnerSize="medium"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  color: golfstatusColors.white,
                  borderRadius: "inherit",
                }}
              />
            )}
            <GSButton
              //emptied rather than dropped, so the spot keeps its size behind
              //the spinner
              style={{
                ...defaultLayouts.fill,
                ...defaultPadding.xLargePad,
                ...(pending ? {visibility: "hidden"} : {}),
              }}
              type="secondary"
              hoverType="none"
              buttonIcon={pending ? undefined : faUsersViewfinder}
              title={
                pending ? "" : selected ? "Select a Team" : "Tap To Assign"
              }
              onClick={
                readOnly
                  ? () => {}
                  : () => currentTournamentTeam?.id
                    ? assignTeamToHoleAssignment(currentTournamentTeam, holePosition, grouping)
                    : selectHoleAssignment({ ...holePosition, grouping })
              }
            ></GSButton>
          </div>
        );
      }
    }

    return (
      <div style={{ ...defaultLayouts.hStack.align(START, CENTER),
                    ...defaultLayouts.fullWidth,
                    ...defaultPadding.mediumLargePad,
                    ...defaultGapping.mediumLargeGap,
                    ...defaultBorders.mediumLargeBorderRadius,
                    backgroundColor: golfstatusColors.grey50 }}>
        {currentRound.startType === "shotgun_start"
          && <div style={{ ...defaultTypography.body }}>{`${holePosition.position}${holePosition.subPosition ?? ""}`}</div>}
        <div style={{ ...defaultLayouts.vStack.align(START, START),
                      ...defaultLayouts.fullWidth,
                      ...defaultGapping.mediumGap }}>{positions}</div>
        {getBlockHole(holePosition)}
      </div>
    );
  };

  const getItems = () => {
    if (holeAssignments === undefined) {
      return [];
    }

    if (currentRound?.startType === "shotgun_start") {
      return (
        <div className="hole-assignment shotgun" style={{...defaultLayouts.fullWidth, ...defaultLayouts.vStack, ...defaultGapping.mediumLargeGap}}>
          <div style={{
            ...defaultLayouts.stickyTop,
            ...defaultLayouts.fullWidth,
            ...defaultLayouts.hStack.align(SPACE_BETWEEN, CENTER),
            backgroundColor: golfstatusColors.white,
            opacity: 1.0,
            padding: `${golfstatusSpacing.mediumLargePad} 0`,
            zIndex: 1,
          }}>
            <div style={{...defaultLayouts.vStack}}>
              <div className="hole-number">Hole {holeAssignments[0].hole}</div>
              <div className="hole-info" style={defaultTypography.uppercase}>Par {holeAssignments[0].holePar}</div>
            </div>
            <div className="hole-info" style={defaultTypography.uppercase}>{holeAssignments[0].roundCourse?.course?.name}</div>
          </div>
          {getAssignments()}
          {getAddGroup()}
        </div>
      );
    } else {
      return (
        <div className="hole-assignment tee-time" style={{...defaultLayouts.fullWidth, ...defaultLayouts.vStack, ...defaultGapping.mediumLargeGap}}>
          <div style={{
            ...defaultLayouts.stickyTop,
            ...defaultLayouts.fullWidth,
            ...defaultLayouts.hStack.align(SPACE_BETWEEN, CENTER),
            backgroundColor: golfstatusColors.white,
            padding: `${golfstatusSpacing.mediumLargePad} 0`,
            zIndex: 1,
          }}>
            <div style={{...defaultLayouts.vStack}}>
              <div className="hole-number">{getTime(holeAssignments[0].teeTime)}</div>
              <div className="hole-info" style={defaultTypography.uppercase}>Hole {holeAssignments[0].hole}</div>
            </div>
            <div className="hole-info" style={defaultTypography.uppercase}>{holeAssignments[0].roundCourse?.course?.name}</div>
          </div>
          {getAssignments()}
          {getAddGroup()}
        </div>
      );
    }
  };

  return (
    <tournament-round-hole-assignment-list-item>
      {getItems()}
    </tournament-round-hole-assignment-list-item>
  );
};

export default TournamentRoundHoleAssignmentListItem;
